import * as React from 'react';
import classNames from 'classnames';
import { styled } from 'naan/stitches.config';
import { paletteKey } from '../../../study/views/study-palette';
import { PlainMarkdown } from '@naan/primitives/text';
import { SimpleNotationsListPanel } from 'study/views/notations/simple-notations-list';
import { SentenceId } from '@tikka/basic-types';
import { usePlayerModel } from '../player-model-context';
import { SentenceToolbar } from './sentence-toolbar';
import { StudyModel } from 'study/models/study-model';
import { isStudyModel } from 'player/models/player-model-handle';
// import { createLogger } from '@common/log';
import { useReaction } from '@common/hooks/use-reaction';

// const log = createLogger('sentence-extras');

const Wrapper = styled('div', {
  '& > .l1': {
    '& .translation-inner': {
      padding: '0 16px 16px 16px',
      // background: 'cyan',
      textStyle: 'body',
      color: paletteKey('$$sentence__inlineTranslationColor'),
    },
  },

  '& .expandable': {
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: 'grid-template-rows .3s',
    '& > div.expandable-inner': {
      gridRow: '1 / span 2',
      overflow: 'hidden',
    },

    '&.expanded': {
      gridTemplateRows: '1fr',
    },
  },
});

const StudySentenceExtras: React.FC<{
  model: StudyModel;
  translation: string;
  sentenceId: SentenceId /*onUnredact: () => void*/;
}> = ({ model, translation, sentenceId }) => {
  const translationRef = React.useRef<HTMLDivElement>(null);
  const notationsRef = React.useRef<HTMLDivElement>(null);

  useReaction(
    () => model.shouldDisplaySentenceInlineTranslation(sentenceId),
    showing => {
      const element = translationRef.current;
      if (element) {
        if (showing) {
          element.classList.add('expanded');
        } else {
          element.classList.remove('expanded');
        }
      }
    }
  );

  useReaction(
    () => model.shouldDisplaySentenceInlineNotations(sentenceId),
    showing => {
      const element = notationsRef.current;
      if (element) {
        if (showing) {
          element.classList.add('expanded');
        } else {
          element.classList.remove('expanded');
        }
      }
    }
  );

  return (
    <Wrapper>
      <div ref={translationRef} className={classNames('l1 expandable')}>
        <div className="expandable-inner">
          <div className="translation-inner">
            <PlainMarkdown source={translation} />
          </div>
        </div>
      </div>
      <div ref={notationsRef} className={classNames('notations expandable')}>
        <div className="expandable-inner">
          <SimpleNotationsListPanel sentenceId={sentenceId} />
        </div>
      </div>
      <SentenceToolbar model={model} sentenceId={sentenceId} />
    </Wrapper>
  );
};

export const SentenceExtrasGeneral: React.FC<{
  translation: string;
  sentenceId: SentenceId /*onUnredact: () => void*/;
}> = ({ translation, sentenceId }) => {
  const model = usePlayerModel();
  if (!isStudyModel(model) || model.fluentListenMode) {
    // don't show anything if we're not in study mode
    return null;
  }

  return (
    <StudySentenceExtras
      translation={translation}
      sentenceId={sentenceId}
      model={model}
    />
  );
};
